import { supabase } from './supabase'

// ── Cost codes ────────────────────────────────────────────────────────────────

export async function loadCostCodesForJob(jobId) {
  const { data, error } = await supabase
    .from('cost_codes')
    .select('cost_code_no, description, phase_no')
    .eq('job_id', jobId)
    .order('cost_code_no', { ascending: true })
  if (error) { console.error('loadCostCodesForJob:', error); return [] }
  return data || []
}

// ── Field forms ───────────────────────────────────────────────────────────────

// ── Email notifications ───────────────────────────────────────────────────────
export async function sendFormNotification(type, formData) {
  try {
    console.log('Sending notification:', type, formData)
    const res = await fetch('/api/notify-form', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, ...formData })
    })
    const data = await res.json()
    console.log('Notification response:', res.status, data)
  } catch (e) {
    console.error('sendFormNotification error:', e)
  }
}

export async function loadFieldForms(filters = {}) {
  let query = supabase.from('field_forms').select('*').order('date', { ascending: false })
  if (filters.status) query = query.eq('status', filters.status)
  if (filters.date) query = query.eq('date', filters.date)
  const { data, error } = await query
  if (error) { console.error('loadFieldForms:', error); return [] }
  // For foreman: filter by email OR by foreman_name if email not set on form
  if (filters.foremanEmail && filters.foremanName) {
    return (data || []).filter(f =>
      f.foreman_email === filters.foremanEmail ||
      (!f.foreman_email && f.foreman_name === filters.foremanName)
    )
  }
  if (filters.foremanEmail) {
    return (data || []).filter(f => f.foreman_email === filters.foremanEmail)
  }
  return data || []
}

export async function loadFieldForm(formId) {
  const { data: form, error } = await supabase
    .from('field_forms').select('*').eq('id', formId).single()
  if (error) { console.error('loadFieldForm:', error); return null }
  const { data: entries } = await supabase.from('form_entries').select('*').eq('form_id', formId)
  const { data: concrete } = await supabase.from('form_concrete').select('*').eq('form_id', formId)
  const { data: masonry } = await supabase.from('form_masonry').select('*').eq('form_id', formId)
  return { ...form, entries: entries || [], concrete: concrete || [], masonry: masonry || [] }
}

export async function generateReferenceNo() {
  try {
    // Use max reference_no + 1 to avoid duplicates when creating multiple forms at once
    const { data, error } = await supabase
      .from('field_forms')
      .select('reference_no')
      .order('reference_no', { ascending: false })
      .limit(1)
    if (error) console.error('generateReferenceNo error:', error)
    if (data && data.length > 0) {
      const lastNum = parseInt(data[0].reference_no) || 0
      return String(lastNum + 1).padStart(3, '0')
    }
    return '001'
  } catch(e) {
    console.error('generateReferenceNo exception:', e)
    return String(Date.now()).slice(-4)
  }
}

export async function createFieldForm(formData, userId, refNo) {
  // Check if a form already exists for this foreman + job + date
  const { data: existing } = await supabase
    .from('field_forms')
    .select('id, reference_no, status')
    .eq('job_id', formData.job_id)
    .eq('date', formData.date)
    .eq('foreman_name', formData.foreman_name)
    .maybeSingle()

  if (existing) {
    if (existing.status !== 'draft') {
      // Already submitted or approved — don't overwrite
      console.log('Form already submitted/approved for', formData.foreman_name, formData.date, '- skipping')
      return existing
    }
    // Draft exists — overwrite it with updated crew/job info
    const { data, error } = await supabase
      .from('field_forms')
      .update({ ...formData, updated_at: new Date().toISOString() })
      .eq('id', existing.id)
      .select().single()
    if (error) { console.error('updateFieldForm:', error); return null }
    console.log('Overwrote draft form for', formData.foreman_name, formData.date)
    return data
  }

  // No existing form — create new one
  // Set radio button fields to null so they appear unselected
  const finalRefNo = refNo || await generateReferenceNo()
  const { data, error } = await supabase
    .from('field_forms')
    .insert({ ...formData, reference_no: finalRefNo, created_by: userId,
      non_contract_work: null, safety_issue: null, delivery_no_receipt: null })
    .select().single()
  if (error) { console.error('createFieldForm:', error); return null }
  return data
}

export async function saveFieldForm(formId, formData, entries, concrete, masonry, userId, userName) {
  // Update main form with last saved timestamp and user
  const { error: formErr } = await supabase
    .from('field_forms').update({
      ...formData,
      updated_at: new Date().toISOString(),
      last_saved_by: userName || null,
    }).eq('id', formId)
  if (formErr) { console.error('saveFieldForm:', formErr); return false }

  // Replace entries — keep ALL workers including those with 0 hours
  await supabase.from('form_entries').delete().eq('form_id', formId)
  if (entries.length > 0) {
    // Strip UI-only flags before saving, ensure employee_id is integer or null
    const cleanEntries = entries
      .filter(e => e.employee_name) // must have a name
      .map(({ _added, _scheduled, ...e }) => ({
        form_id: formId,
        employee_id: e.employee_id ? parseInt(e.employee_id) : 0,
        employee_name: e.employee_name || '',
        hours: e.hours === '' || e.hours === null || e.hours === undefined ? 0 : parseFloat(e.hours),
        cost_code_no: e.cost_code_no !== '' && e.cost_code_no !== null && e.cost_code_no !== undefined ? Number(e.cost_code_no) : null,
        cost_code_desc: e.cost_code_desc || '',
        notes: e.notes || '',
      }))
    console.log('Saving entries:', JSON.stringify(cleanEntries))
    if (cleanEntries.length > 0) {
      const { error: entryErr } = await supabase.from('form_entries').insert(cleanEntries)
      if (entryErr) console.error('form_entries insert error:', JSON.stringify(entryErr))
    }
  }

  // Replace concrete — only save rows that have a cost code or qty
  await supabase.from('form_concrete').delete().eq('form_id', formId)
  const cleanConcrete = concrete.filter(c => c.cost_code_no || c.quantity)
  if (cleanConcrete.length > 0) {
    const { error: concreteErr } = await supabase.from('form_concrete').insert(
      cleanConcrete.map(({ ...c }) => ({
        form_id: formId,
        cost_code_no: c.cost_code_no ? Number(c.cost_code_no) : null,
        cost_code_desc: c.cost_code_desc || '',
        quantity: c.quantity ? parseFloat(c.quantity) : 0,
        notes: c.notes || '',
      }))
    )
    if (concreteErr) console.error('form_concrete insert error:', concreteErr)
  }

  // Replace masonry
  await supabase.from('form_masonry').delete().eq('form_id', formId)
  if (masonry.length > 0) {
    await supabase.from('form_masonry').insert(masonry.map(m => ({ ...m, form_id: formId })))
  }

  return true
}

export async function submitFieldForm(formId, userName) {
  const { error } = await supabase.from('field_forms').update({
    status: 'submitted',
    submitted_at: new Date().toISOString(),
    last_saved_by: userName || null,
  }).eq('id', formId)
  if (error) { console.error('submitFieldForm:', error); return false }
  return true
}

export async function approveFieldForm(formId, userName) {
  // Get the form and all its entries
  const form = await loadFieldForm(formId)
  if (!form) return false

  // Write to hours ledger
  const ledgerRows = form.entries.map(e => ({
    form_id: formId,
    employee_id: e.employee_id,
    employee_name: e.employee_name,
    job_id: form.job_id,
    job_no: String(form.job_id),
    cost_code_no: e.cost_code_no,
    work_date: form.date,
    hours: e.hours,
    approved_by: userName,
    approved_at: new Date().toISOString(),
  }))

  if (ledgerRows.length > 0) {
    // Only insert ledger rows with hours > 0 and valid cost code
    const validRows = ledgerRows.filter(r => r.hours > 0 && r.cost_code_no)
    if (validRows.length > 0) {
      const { error: ledgerErr } = await supabase.from('hours_ledger').insert(validRows)
      if (ledgerErr) console.error('hours ledger error:', JSON.stringify(ledgerErr))
      // Don't return false — still approve even if ledger fails
    }
  }

  // Update form status
  const { error } = await supabase.from('field_forms').update({
    status: 'approved',
    approved_by: userName,
    approved_at: new Date().toISOString(),
  }).eq('id', formId)
  if (error) { console.error('approveFieldForm:', error); return false }
  return true
}

export async function updateFormStatus(formId, status, userName, notes) {
  const updates = { status }
  if (status === 'reviewed') {
    updates.reviewed_by = userName
    updates.reviewed_at = new Date().toISOString()
    if (notes) updates.reviewer_notes = notes
  }
  const { error } = await supabase.from('field_forms').update(updates).eq('id', formId)
  if (error) { console.error('updateFormStatus:', error); return false }
  return true
}

// ── Hours ledger / payroll export ─────────────────────────────────────────────

export async function loadHoursForWeek(startDate, endDate) {
  const { data, error } = await supabase
    .from('hours_ledger')
    .select('*')
    .gte('work_date', startDate)
    .lte('work_date', endDate)
    .order('work_date', { ascending: true })
  if (error) { console.error('loadHoursForWeek:', error); return [] }
  return data || []
}

export function exportPayrollCSV(hoursRows) {
  const header = 'employee number,job no,Cost code,Date,Hours'
  const rows = hoursRows.map(r => {
    // Parse YYYY-MM-DD without timezone shift
    const parts = (r.work_date || '').split('-')
    const date = parts.length === 3 ? `${parseInt(parts[1])}/${parseInt(parts[2])}/${parts[0]}` : r.work_date
    return `${r.employee_id},${r.job_no},${r.cost_code_no},${date},${r.hours}`
  })
  return [header, ...rows].join('\n')
}
